import { useState } from 'react';
import {
  HStack,
  Input,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { XIcon } from 'lucide-react';
import { LabOption, LabVendor } from '../../../api/types';
import {
  MIN_LAB_OPTION_SEARCH,
  useLabOptions,
} from '../../../api/healthCoach/useLabOptions';

type Props = {
  vendor: LabVendor;
  selected: LabOption[];
  onChange: (selected: LabOption[]) => void;
};

export const LabTestPicker = ({ vendor, selected, onChange }: Props) => {
  const [query, setQuery] = useState('');
  const { labOptions, isLoading, isError } = useLabOptions(query, vendor);

  const selectedIds = new Set(selected.map((option) => option.id));
  // The endpoint already filters to the selected vendor; just drop anything
  // already selected.
  const results = labOptions.filter((option) => !selectedIds.has(option.id));

  const addTest = (option: LabOption) => {
    onChange([...selected, option]);
    setQuery('');
  };

  const removeTest = (id: string) => {
    onChange(selected.filter((option) => option.id !== id));
  };

  const showResults = query.length >= MIN_LAB_OPTION_SEARCH;

  return (
    <VStack align={'stretch'} gap={2}>
      <Text
        fontSize={'2xs'}
        fontWeight={700}
        color={'bluegrey'}
        textTransform={'uppercase'}
        letterSpacing={'0.04em'}
      >
        Tests
      </Text>

      {selected.length > 0 && (
        <HStack flexWrap={'wrap'} gap={2}>
          {selected.map((option) => (
            <HStack
              key={option.id}
              gap={1}
              align={'center'}
              bg={'cloud.50'}
              borderWidth={1}
              borderColor={'cloud.200'}
              rounded={'full'}
              px={3}
              py={1}
            >
              <Text fontSize={'xs'} color={'secondary.500'}>
                {option.name}
              </Text>
              <Pressable
                aria-label={`Remove ${option.name}`}
                color={'bluegrey'}
                onClick={() => removeTest(option.id)}
              >
                <XIcon size={12} />
              </Pressable>
            </HStack>
          ))}
        </HStack>
      )}

      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={'Search for tests (min 3 characters)'}
      />

      {showResults && (
        <VStack
          align={'stretch'}
          gap={0}
          borderWidth={1}
          borderColor={'cloud.200'}
          rounded={'lg'}
          bg={'white'}
          maxH={'220px'}
          overflowY={'auto'}
        >
          {isLoading ? (
            <Text fontSize={'sm'} color={'bluegrey'} p={3}>
              Searching…
            </Text>
          ) : isError ? (
            <Text fontSize={'sm'} color={'red.500'} p={3}>
              Something went wrong loading tests. Please try again.
            </Text>
          ) : results.length === 0 ? (
            <Text fontSize={'sm'} color={'bluegrey'} p={3}>
              No {vendor} tests match "{query}"
            </Text>
          ) : (
            results.map((option) => (
              <Pressable
                key={option.id}
                onClick={() => addTest(option)}
                w={'100%'}
                px={3}
                py={2}
                textAlign={'left'}
                _hover={{ bg: 'feather' }}
              >
                <Text fontSize={'sm'} color={'secondary.500'}>
                  {option.name}
                </Text>
              </Pressable>
            ))
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default LabTestPicker;
